import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Typography, Container, InputAdornment, TextField,
    Chip, Slider, Button, Skeleton, Drawer, IconButton,
    MenuItem, Select, FormControl, InputLabel, Stack, Divider,
    useTheme, useMediaQuery, Card, CardMedia
} from '@mui/material';
import SearchIcon          from '@mui/icons-material/Search';
import CloseIcon           from '@mui/icons-material/Close';
import FilterListIcon      from '@mui/icons-material/FilterList';
import LocationOnIcon      from '@mui/icons-material/LocationOn';
import AccessTimeIcon      from '@mui/icons-material/AccessTime';
import DirectionsBikeIcon  from '@mui/icons-material/DirectionsBike';
import DirectionsBusIcon   from '@mui/icons-material/DirectionsBus';
import DirectionsWalkIcon  from '@mui/icons-material/DirectionsWalk';
import FlagIcon            from '@mui/icons-material/Flag';
import { useNavigate }     from 'react-router-dom';
import { useTranslation }  from 'react-i18next';
import axiosPublic         from '../api/axiosPublic';
import axiosAuth           from '../api/axiosConfig';
import { useAuth }         from '../context/AuthContext';
import { useCurrency }     from '../context/CurrencyContext';
import { formatCurrency }  from '../utils/currency';

const TERRACOTTA = '#C4622D';
const WALNUT     = '#2C1F14';
const FOREST     = '#4A6741';
const PARCHMENT  = '#F7F3EE';
const STONE      = '#EDE8E1';

const TYPE_OPTIONS = [
    { value: 'ALL',  label: 'All types', icon: null },
    { value: 'WALK', label: 'Walking',   icon: <DirectionsWalkIcon sx={{ fontSize: '1rem' }} /> },
    { value: 'BIKE', label: 'Cycling',   icon: <DirectionsBikeIcon sx={{ fontSize: '1rem' }} /> },
    { value: 'BUS',  label: 'Bus',       icon: <DirectionsBusIcon  sx={{ fontSize: '1rem' }} /> },
];

const SORT_OPTIONS = [
    { value: 'DEFAULT',    label: 'Recommended' },
    { value: 'PRICE_ASC',  label: 'Price: Low → High' },
    { value: 'PRICE_DESC', label: 'Price: High → Low' },
    { value: 'NAME_ASC',   label: 'Name A–Z' },
];

function ResultCardSkeleton() {
    return (
        <Box sx={{ display: 'flex', borderRadius: '16px', overflow: 'hidden', bgcolor: 'white',
            border: '1px solid rgba(196,98,45,0.1)', boxShadow: '0 2px 8px rgba(44,31,20,0.06)', mb: 2 }}>
            <Skeleton variant="rectangular" width={180} height={160} sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <Box>
                    <Skeleton variant="text" width="60%" height={28} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width="40%" height={18} sx={{ mb: 1 }} />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Skeleton variant="rounded" width={70} height={22} sx={{ borderRadius: '20px' }} />
                        <Skeleton variant="rounded" width={90} height={22} sx={{ borderRadius: '20px' }} />
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Skeleton variant="text" width={60} height={32} />
                    <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: '20px' }} />
                </Box>
            </Box>
        </Box>
    );
}

function FiltersPanel({ search, setSearch, typeFilter, setTypeFilter, sort, setSort,
    priceSlider, setPriceSlider, priceCeiling, durationFilter, setDurationFilter,
    startPointFilter, setStartPointFilter, onClear, isFiltered, currency, count }) {

    const chipSx = (active) => ({
        fontWeight: 700, fontSize: '0.78rem', borderRadius: '20px',
        transition: 'all 0.2s', cursor: 'pointer',
        bgcolor: active ? TERRACOTTA : 'white',
        color:   active ? 'white'    : WALNUT,
        borderColor: active ? TERRACOTTA : 'rgba(196,98,45,0.25)',
        '& .MuiChip-icon': { color: active ? 'white' : TERRACOTTA },
        '&:hover': { bgcolor: active ? '#9A4A1F' : 'rgba(196,98,45,0.06)', borderColor: TERRACOTTA },
    });

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Search */}
            <TextField size="small" placeholder="Search tours…"
                       value={search} onChange={e => setSearch(e.target.value)}
                       InputProps={{
                           startAdornment: (
                               <InputAdornment position="start">
                                   <SearchIcon sx={{ color: 'text.secondary', fontSize: '1.1rem' }} />
                               </InputAdornment>
                           ),
                           endAdornment: search ? (
                               <InputAdornment position="end">
                                   <IconButton size="small" onClick={() => setSearch('')}>
                                       <CloseIcon sx={{ fontSize: '0.9rem' }} />
                                   </IconButton>
                               </InputAdornment>
                           ) : null,
                       }}
                       sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white',
                           '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TERRACOTTA } } }} />

            <Divider />

            {/* Type */}
            <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: WALNUT, textTransform: 'uppercase',
                    letterSpacing: '0.08em', fontSize: '0.7rem', mb: 1.2, display: 'block' }}>
                    Tour Type
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                    {TYPE_OPTIONS.map(opt => (
                        <Chip key={opt.value} label={opt.label}
                              icon={opt.icon || undefined}
                              onClick={() => setTypeFilter(opt.value)}
                              variant={typeFilter === opt.value ? 'filled' : 'outlined'}
                              sx={chipSx(typeFilter === opt.value)} />
                    ))}
                </Box>
            </Box>

            <Divider />

            {/* Price */}
            <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: WALNUT, textTransform: 'uppercase',
                    letterSpacing: '0.08em', fontSize: '0.7rem', mb: 1.2, display: 'block' }}>
                    Max Price
                </Typography>
                <Slider value={priceSlider} min={0} max={priceCeiling} step={5}
                        onChange={(_, v) => setPriceSlider(v)}
                        sx={{ color: TERRACOTTA,
                            '& .MuiSlider-thumb': { width: 16, height: 16 },
                            '& .MuiSlider-track': { height: 4 },
                            '& .MuiSlider-rail':  { height: 4, opacity: 0.2 } }} />
                <Typography variant="caption" sx={{ color: priceSlider < priceCeiling ? TERRACOTTA : 'text.secondary', fontWeight: 700 }}>
                    {priceSlider < priceCeiling
                        ? `Up to ${formatCurrency(priceSlider, currency)}`
                        : 'Any price'}
                </Typography>
            </Box>

            <Divider />

            {/* Duration */}
            <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: WALNUT, textTransform: 'uppercase',
                    letterSpacing: '0.08em', fontSize: '0.7rem', mb: 1.2, display: 'block' }}>
                    Duration
                </Typography>
                <TextField size="small" placeholder="e.g. 2 hours" fullWidth
                           value={durationFilter} onChange={e => setDurationFilter(e.target.value)}
                           sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white',
                               '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TERRACOTTA } } }} />
            </Box>

            <Divider />

            {/* Start point */}
            <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: WALNUT, textTransform: 'uppercase',
                    letterSpacing: '0.08em', fontSize: '0.7rem', mb: 1.2, display: 'block' }}>
                    Start Point
                </Typography>
                <TextField size="small" placeholder="e.g. Yerevan" fullWidth
                           value={startPointFilter} onChange={e => setStartPointFilter(e.target.value)}
                           sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'white',
                               '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TERRACOTTA } } }} />
            </Box>

            <Divider />

            {/* Sort */}
            <Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: WALNUT, textTransform: 'uppercase',
                    letterSpacing: '0.08em', fontSize: '0.7rem', mb: 1.2, display: 'block' }}>
                    Sort By
                </Typography>
                <FormControl size="small" fullWidth>
                    <Select value={sort} onChange={e => setSort(e.target.value)}
                            sx={{ borderRadius: '12px', bgcolor: 'white',
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: TERRACOTTA } }}>
                        {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {isFiltered && (
                <>
                    <Divider />
                    <Button size="small" startIcon={<CloseIcon sx={{ fontSize: '0.9rem' }} />}
                            onClick={onClear} fullWidth
                            sx={{ color: TERRACOTTA, border: '1px solid rgba(196,98,45,0.35)',
                                borderRadius: '20px', fontWeight: 600, fontSize: '0.78rem',
                                '&:hover': { bgcolor: 'rgba(196,98,45,0.06)', borderColor: TERRACOTTA } }}>
                        Clear all filters
                    </Button>
                </>
            )}

            <Typography variant="caption" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                {count} {count === 1 ? 'tour' : 'tours'} found
            </Typography>
        </Box>
    );
}

export default function TourOffers() {
    const navigate             = useNavigate();
    const { t }                = useTranslation();
    const { user }             = useAuth();
    const { currency, convert } = useCurrency();
    const theme                 = useTheme();
    const isMobile              = useMediaQuery(theme.breakpoints.down('md'));
    const isStaff               = user?.role === 'MANAGER' || user?.role === 'ADMIN';

    const [tours,   setTours]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // Filters
    const [search,          setSearch]          = useState('');
    const [typeFilter,      setTypeFilter]      = useState('ALL');
    const [sort,            setSort]            = useState('DEFAULT');
    const [priceSlider,     setPriceSlider]     = useState(0);
    const [durationFilter,  setDurationFilter]  = useState('');
    const [startPointFilter, setStartPointFilter] = useState('');

    useEffect(() => {
        const url = isStaff ? '/api/tourOffers/view/all?size=100' : '/api/tourOffers/view?size=100';
        const req = isStaff ? axiosAuth.get(url) : axiosPublic.get(url);
        req
            .then(res => setTours(Array.isArray(res.data) ? res.data : (res.data?.content || [])))
            .catch(() => setTours([]))
            .finally(() => setLoading(false));
    }, [isStaff]);

    const priceCeiling = useMemo(() => {
        if (!tours.length) return 500;
        return Math.ceil(Math.max(...tours.map(t => convert(t.basePrice || 0, t.currency || 'EUR')), 0) / 10) * 10;
    }, [tours, convert]);

    useEffect(() => { setPriceSlider(priceCeiling); }, [priceCeiling]);

    const isFiltered = search.trim() !== '' || typeFilter !== 'ALL' || priceSlider < priceCeiling
        || durationFilter.trim() !== '' || startPointFilter.trim() !== '' || sort !== 'DEFAULT';

    const clearFilters = () => {
        setSearch(''); setTypeFilter('ALL'); setPriceSlider(priceCeiling);
        setDurationFilter(''); setStartPointFilter(''); setSort('DEFAULT');
    };

    const filtered = useMemo(() => {
        const q  = search.trim().toLowerCase();
        const sp = startPointFilter.trim().toLowerCase();
        const dur = durationFilter.trim().toLowerCase();

        let result = tours.filter(t => {
            const matchSearch   = !q  || (t.title || '').toLowerCase().includes(q) || (t.place || '').toLowerCase().includes(q);
            const matchType     = typeFilter === 'ALL' || t.type === typeFilter;
            const matchPrice    = priceSlider >= priceCeiling || convert(t.basePrice || 0, t.currency || 'EUR') <= priceSlider;
            const matchDuration = !dur || (t.duration || '').toLowerCase().includes(dur);
            const matchStart    = !sp  || (t.startPoint || '').toLowerCase().includes(sp);
            return matchSearch && matchType && matchPrice && matchDuration && matchStart;
        });

        if (sort === 'PRICE_ASC')  result = [...result].sort((a, b) => convert(a.basePrice || 0, a.currency || 'EUR') - convert(b.basePrice || 0, b.currency || 'EUR'));
        if (sort === 'PRICE_DESC') result = [...result].sort((a, b) => convert(b.basePrice || 0, b.currency || 'EUR') - convert(a.basePrice || 0, a.currency || 'EUR'));
        if (sort === 'NAME_ASC')   result = [...result].sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        return result;
    }, [tours, search, typeFilter, priceSlider, priceCeiling, durationFilter, startPointFilter, sort, convert]);

    const getTourIcon = (type) => {
        if (type === 'BIKE') return <DirectionsBikeIcon sx={{ fontSize: '1rem' }} />;
        if (type === 'BUS')  return <DirectionsBusIcon  sx={{ fontSize: '1rem' }} />;
        if (type === 'WALK') return <DirectionsWalkIcon sx={{ fontSize: '1rem' }} />;
        return null;
    };

    const filterProps = {
        search, setSearch, typeFilter, setTypeFilter, sort, setSort,
        priceSlider, setPriceSlider, priceCeiling,
        durationFilter, setDurationFilter,
        startPointFilter, setStartPointFilter,
        onClear: clearFilters, isFiltered, currency,
        count: loading ? 0 : filtered.length,
    };

    return (
        <Box sx={{ width: '100%', minHeight: '100vh', background: `linear-gradient(rgba(247,243,238,0.84), rgba(247,243,238,0.84)), url("https://explorearmenia.am/images/2025/09/20/ea-background-min.webp") center/cover fixed` }}>
            {/* ── Dark header ──────────────────────────────────────────────── */}
            <Box sx={{ bgcolor: WALNUT, py: 5, px: { xs: 2, md: 6 } }}>
                <Typography variant="overline" sx={{ color: '#C9973A', letterSpacing: '0.2em', fontWeight: 700 }}>
                    Discover Armenia
                </Typography>
                <Typography variant="h4" fontWeight={800}
                            sx={{ color: 'white', fontFamily: "'Playfair Display', Georgia, serif", mb: 2.5 }}>
                    Browse All Tours
                </Typography>
                <TextField fullWidth placeholder="Search by name or destination…"
                           value={search} onChange={e => setSearch(e.target.value)}
                           InputProps={{
                               startAdornment: (
                                   <InputAdornment position="start">
                                       <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                                   </InputAdornment>
                               ),
                               endAdornment: search ? (
                                   <InputAdornment position="end">
                                       <IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                           <CloseIcon sx={{ fontSize: '1rem' }} />
                                       </IconButton>
                                   </InputAdornment>
                               ) : null,
                           }}
                           sx={{ maxWidth: 600,
                               '& .MuiOutlinedInput-root': {
                                   borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.1)',
                                   color: 'white',
                                   '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
                                   '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
                                   '&.Mui-focused fieldset': { borderColor: TERRACOTTA },
                               },
                               '& input::placeholder': { color: 'rgba(255,255,255,0.45)', opacity: 1 },
                           }} />
            </Box>

            {/* ── Body: sidebar + cards ─────────────────────────────────────── */}
            <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 5 } }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>

                    {/* ── Desktop sidebar ──────────────────────────────────── */}
                    {!isMobile && (
                        <Box sx={{ width: 280, flexShrink: 0, position: 'sticky', top: 80,
                            bgcolor: 'white', borderRadius: '16px', p: 2.5,
                            border: '1px solid rgba(196,98,45,0.1)',
                            boxShadow: '0 2px 12px rgba(44,31,20,0.07)' }}>
                            <Typography variant="subtitle1" fontWeight={800} sx={{ color: WALNUT, mb: 2,
                                display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FilterListIcon sx={{ color: TERRACOTTA, fontSize: '1.2rem' }} />
                                Filters
                            </Typography>
                            <FiltersPanel {...filterProps} />
                        </Box>
                    )}

                    {/* ── Main content ─────────────────────────────────────── */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>

                        {/* Mobile filter button + result count */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2.5 }}>
                            {isMobile && (
                                <Button startIcon={<FilterListIcon />} variant="outlined"
                                        onClick={() => setDrawerOpen(true)}
                                        sx={{ borderColor: 'rgba(196,98,45,0.4)', color: TERRACOTTA,
                                            borderRadius: '20px', fontWeight: 700,
                                            '&:hover': { borderColor: TERRACOTTA, bgcolor: 'rgba(196,98,45,0.06)' } }}>
                                    Filters {isFiltered && '•'}
                                </Button>
                            )}
                            <Box sx={{ ml: isMobile ? 0 : 'auto' }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                                    {loading ? 'Loading…' : `${filtered.length} ${filtered.length === 1 ? 'tour' : 'tours'} found`}
                                </Typography>
                            </Box>
                            {!isMobile && (
                                <FormControl size="small" sx={{ minWidth: 180, ml: 2 }}>
                                    <InputLabel>Sort by</InputLabel>
                                    <Select value={sort} onChange={e => setSort(e.target.value)} label="Sort by"
                                            sx={{ borderRadius: '12px', bgcolor: 'white' }}>
                                        {SORT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        </Box>

                        {/* Results */}
                        {loading ? (
                            Array.from({ length: 5 }).map((_, i) => <ResultCardSkeleton key={i} />)
                        ) : filtered.length === 0 ? (
                            <Box sx={{ py: 10, textAlign: 'center', bgcolor: 'white',
                                borderRadius: '20px', border: '2px dashed rgba(196,98,45,0.2)' }}>
                                <Typography variant="h6" sx={{ color: WALNUT, fontWeight: 700, mb: 1 }}>
                                    No tours match your filters
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    Try adjusting or clearing your filters.
                                </Typography>
                                <Button variant="outlined" onClick={clearFilters}
                                        sx={{ borderRadius: '20px', color: TERRACOTTA, borderColor: TERRACOTTA, fontWeight: 700 }}>
                                    Clear filters
                                </Button>
                            </Box>
                        ) : (
                            filtered.map(tour => {
                                const price = convert(tour.basePrice || 0, tour.currency || 'EUR');
                                return (
                                    <Box key={tour.id} component="a" href={`/tours/${tour.id}`}
                                         sx={{ display: 'flex', mb: 2, borderRadius: '16px', overflow: 'hidden',
                                             bgcolor: 'white', cursor: 'pointer', textDecoration: 'none', color: 'inherit',
                                             border: '1px solid rgba(196,98,45,0.1)',
                                             boxShadow: '0 2px 8px rgba(44,31,20,0.06)',
                                             transition: 'all 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                                             '&:hover': { transform: 'translateY(-3px)',
                                                 boxShadow: '0 12px 28px rgba(196,98,45,0.15)',
                                                 borderColor: 'rgba(196,98,45,0.3)' } }}>

                                        {/* Image */}
                                        <Box sx={{ width: { xs: 120, sm: 200 }, flexShrink: 0,
                                            overflow: 'hidden', position: 'relative' }}>
                                            {tour.imageUrl ? (
                                                <Box component="img" src={tour.imageUrl} alt={tour.title}
                                                     sx={{ width: '100%', height: '100%', objectFit: 'cover',
                                                         transition: 'transform 0.4s ease',
                                                         '&:hover': { transform: 'scale(1.05)' } }} />
                                            ) : (
                                                <Box sx={{ width: '100%', height: '100%', minHeight: 140,
                                                    background: `linear-gradient(135deg, ${TERRACOTTA} 0%, #9A4A1F 100%)`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {getTourIcon(tour.type)}
                                                </Box>
                                            )}
                                        </Box>

                                        {/* Info */}
                                        <Box sx={{ flex: 1, p: { xs: 1.5, sm: 2.5 }, display: 'flex',
                                            flexDirection: 'column', justifyContent: 'space-between', minWidth: 0 }}>
                                            <Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                                                    <Chip label={tour.type} size="small" icon={getTourIcon(tour.type) || undefined}
                                                          sx={{ fontSize: '0.7rem', fontWeight: 700, height: 22,
                                                              bgcolor: 'rgba(196,98,45,0.08)', color: TERRACOTTA,
                                                              border: '1px solid rgba(196,98,45,0.2)' }} />
                                                    {isStaff && tour.status === 'INACTIVE' && (
                                                        <Chip label="INACTIVE" size="small"
                                                              sx={{ fontSize: '0.65rem', fontWeight: 800, height: 20,
                                                                  bgcolor: 'rgba(150,0,0,0.1)', color: '#8B0000',
                                                                  border: '1px solid rgba(150,0,0,0.3)' }} />
                                                    )}
                                                </Box>
                                                <Typography variant="h6" fontWeight={800}
                                                            sx={{ fontFamily: "'Playfair Display', Georgia, serif",
                                                                color: WALNUT, lineHeight: 1.25, mb: 0.5,
                                                                fontSize: { xs: '1rem', sm: '1.15rem' } }}>
                                                    {tour.title}
                                                </Typography>
                                                <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                                    {tour.place && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                            <LocationOnIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="text.secondary">{tour.place}</Typography>
                                                        </Box>
                                                    )}
                                                    {tour.duration && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                            <AccessTimeIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="text.secondary">{tour.duration}</Typography>
                                                        </Box>
                                                    )}
                                                    {tour.startPoint && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                                            <FlagIcon sx={{ fontSize: '0.85rem', color: 'text.secondary' }} />
                                                            <Typography variant="caption" color="text.secondary">{tour.startPoint}</Typography>
                                                        </Box>
                                                    )}
                                                </Stack>
                                                {tour.description && (
                                                    <Typography variant="body2" color="text.secondary"
                                                                sx={{ mt: 1, display: { xs: 'none', sm: 'block' },
                                                                    overflow: 'hidden', textOverflow: 'ellipsis',
                                                                    display: '-webkit-box', WebkitLineClamp: 2,
                                                                    WebkitBoxOrient: 'vertical' }}>
                                                        {tour.description}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Price + CTA */}
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between',
                                                alignItems: 'flex-end', mt: 2 }}>
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">From</Typography>
                                                    <Typography variant="h6" fontWeight={900}
                                                                sx={{ color: TERRACOTTA, lineHeight: 1 }}>
                                                        {formatCurrency(price, currency)}
                                                    </Typography>
                                                </Box>
                                                <Button variant="contained" size="small"
                                                        component="a" href={`/tours/${tour.id}`}
                                                        onClick={e => e.stopPropagation()}
                                                        sx={{ bgcolor: TERRACOTTA, '&:hover': { bgcolor: '#9A4A1F' },
                                                            borderRadius: '20px', fontWeight: 700, px: 2.5 }}>
                                                    View details
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                );
                            })
                        )}
                    </Box>
                </Box>
            </Container>

            {/* ── Mobile filter drawer ──────────────────────────────────────── */}
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
                    PaperProps={{ sx: { width: 300, p: 2.5 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ color: WALNUT,
                        display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterListIcon sx={{ color: TERRACOTTA }} /> Filters
                    </Typography>
                    <IconButton onClick={() => setDrawerOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
                <FiltersPanel {...filterProps} />
            </Drawer>
        </Box>
    );
}
